import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Container, Tabs, Tab, Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Members from './Members';
import Tenants from './Tenants';
import PermissionsTab from './Permissions';
import RolesTab from './Roles';
import Organizations from './Organizations';
import { UserContext } from '../GlobalContextProviders/UserContext';

/**
 * Admin Dashboard component.
 *
 * This component renders the administrative dashboard with navigation tabs for different admin sections.
 * It supports deep-linking via the route parameter ":section" so that each tab can be accessed directly:
 *   /admin/manage/organizations  (visible only for users with signUpType === "organization")
 *   /admin/manage/tenants
 *   /admin/manage/members
 *   /admin/manage/roles
 *   /admin/manage/permissions
 *   /admin/manage/settings
 *
 * The list of tabs is dynamically computed based on the current user's profile. If the user has a
 * signUpType of "organization", the Organizations tab is shown to allow inviting users to their organization(s).
 *
 * When the route changes, the active tab updates accordingly. When the user changes tabs, the URL is
 * updated to match the selected section. This enables the Admin tabs to be linked individually from
 * the NavbarLeft component.
 *
 * Props allow customizing the visible tab labels without impacting routing. The section-to-route mapping
 * is computed from the tabs array and does not depend on the tab label text. You can safely rename the
 * tabs via the props below:
 * - TenantsName: string label for a single Tenant (singular; default: "Tenant")
 * - TenantsNamePlural: string label for multiple Tenants (plural; default: "Tenants")
 * - MembersName: string label for the Members tab (default: "Member")
 * - RolesName: string label for the Roles tab (default: "Role")
 * - PermissionsName: string label for the Permissions tab (default: "Permission")
 * - SettingsName: string label for the Settings tab (default: "Setting")
 * - OrganizationsName: string label for the Organizations tab (default: "Organization")
 *
 * @component
 * @example
 * return (
 *   <Admin
 *     TenantsName="Workspace"
 *     TenantsNamePlural="Workspaces"
 *     MembersName="User"
 *     RolesName="Access Role"
 *     PermissionsName="Access Rule"
 *     SettingsName="Configuration"
 *     OrganizationsName="Organization"
 *   />
 * )
 */
export default function Admin({
  TenantsName = 'Tenant',
  TenantsNamePlural = 'Tenants',
  MembersName = 'Member',
  RolesName = 'Role',
  PermissionsName = 'Permission',
  SettingsName = 'Setting',
  OrganizationsName = 'Organization',
}) {
	const navigate = useNavigate();
	const { section } = useParams();
	const { user } = useContext(UserContext);

	/**
	 * Build the tabs array dynamically based on the current user.
	 * If the user has signUpType "organization", include Organizations as the first tab.
	 *
	 * Note:
	 * - The tab label for the Tenants section uses the plural form (TenantsNamePlural)
	 *   because it represents a collection. Singular and plural forms are still passed to
	 *   the Tenants component for its own use where appropriate.
	 */
	const tabs = useMemo(() => {
		const items = [];
		if (user?.signUpType === 'organization') {
			items.push({
				key: 'organizations',
				label: OrganizationsName,
				render: <Organizations />
			});
		}
		items.push(
			{
				key: 'tenants',
				label: TenantsNamePlural,
				render: <Tenants TenantsName={TenantsName} TenantsNamePlural={TenantsNamePlural} />
			},
			{ key: 'members', label: MembersName, render: <Members /> },
			// { key: 'roles', label: RolesName, render: <RolesTab /> },
			// { key: 'permissions', label: PermissionsName, render: <PermissionsTab /> },
			// { key: 'settings', label: SettingsName, render: <Typography variant="body1">Settings content goes here.</Typography> },
		);
		return items;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
      user?.signUpType, 
      TenantsName, 
      TenantsNamePlural,
      MembersName, 
      RolesName, 
      PermissionsName, 
      SettingsName, 
      OrganizationsName
    ]);

	// Compute lookups for routing <-> index
	const sectionToIndex = useMemo(() => {
		const map = {};
		tabs.forEach((t, idx) => { map[t.key] = idx; });
		return map;
	}, [tabs]);

	const indexToSection = useMemo(() => tabs.map((t) => t.key), [tabs]);

	// Default to first tab if no recognizable section is present
	const initialTab = sectionToIndex[section] ?? 0;
	const [activeTab, setActiveTab] = useState(initialTab);

	/**
	 * Sync active tab when the route parameter changes. This keeps the UI in sync
	 * if navigation happens from the sidebar or via direct URL entry.
	 */
	useEffect(() => {
		const nextIndex = sectionToIndex[section] ?? 0;
		if (nextIndex !== activeTab) {
			setActiveTab(nextIndex);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [section, sectionToIndex]);

	/**
	 * Handles the event when a tab is changed.
	 *
	 * Updates both component state and the browser URL to reflect the selected section.
	 *
	 * @param {object} event - The event source of the callback.
	 * @param {number} newValue - The value of the new active tab.
	 */
	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
		const nextSection = indexToSection[newValue] ?? tabs[0]?.key ?? 'tenants';
		navigate(`/admin/manage/${nextSection}`, { replace: true });
	};

	return (
		<Container maxWidth="lg">
			<Typography variant="h4" component="h1" gutterBottom>
				Admin Dashboard
			</Typography>
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				aria-label="admin dashboard tabs"
				indicatorColor="primary"
				textColor="primary"
			>
				{tabs.map((t) => (
					<Tab key={t.key} label={t.label} />
				))}
			</Tabs>
			<Box sx={{ mt: 2 }}>
				{tabs.map((t, idx) => (
					<Box key={t.key} role="tabpanel" hidden={activeTab !== idx}>
						{activeTab === idx ? t.render : null}
					</Box>
				))}
			</Box>
		</Container>
	);
}

Admin.propTypes = {
	/**
	 * Custom label for a single Tenant (singular).
	 */
	TenantsName: PropTypes.string,
	/**
	 * Custom label for multiple Tenants (plural).
	 */
	TenantsNamePlural: PropTypes.string,
	/**
	 * Custom label for the Members tab.
	 */
	MembersName: PropTypes.string,
	/**
	 * Custom label for the Roles tab.
	 */
	RolesName: PropTypes.string,
	/**
	 * Custom label for the Permissions tab.
	 */
	PermissionsName: PropTypes.string,
	/**
	 * Custom label for the Settings tab.
	 */
	SettingsName: PropTypes.string,
	/**
	 * Custom label for the Organizations tab.
	 */
	OrganizationsName: PropTypes.string,
};